// import React, { useRef, useState, useEffect } from 'react';
import React, { useRef, useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { Card } from 'tea-component';
import Filter, { IFilterInstance } from '@/pages/intelligence/components/Filter';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import useFilterUseHook from '@/pages/intelligence/hooks/useFilterUseHook';
import useGenreComponent from '@/pages/intelligence/hooks/useGenreComponent';
import useSenTowerTag from '@/pages/intelligence/hooks/useSenTowerTag';
import { updateURLSearchParams, isListEqual } from '@intelligence/utils/commonUtils';
import { getAllCountries } from '@/pages/intelligence/utils/regionSelectUtils';
import { analysisPlatformTopInfo } from '@/pages/intelligence/_config/storeRank/utils';
import { formatFilterSaveValue, handleExtendTimeFilterValue } from '@intelligence/utils/filterUtils';
import { handleRangeDate2SingleDateRangeTimeFilterExtend } from '@intelligence/utils/dateUtils';
import { handleSensorTowerValueFilter } from '@intelligence/utils/topchartsUtils';
import { GENRE_SOURCE_MAP, GENRE_SOURCES_MULTI_DATASOURCE_TOPCHARTS_PAGE } from '@intelligence/components/GenreSelect/constants';
import * as FILTER_KEYS from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { FILTER_DEFAULT_INPUT_LIST, FILTER_DEFAULT_VALUE } from './_config';
import './index.scss';
import type { IBaseProps, IRegion } from '@/pages/intelligence/types/common';
import type { IMobileStoreRankMoreFilter, IStoreRankMoreCoreFetchParams } from '@/pages/intelligence/types/storeRank';

interface IMobileStoreRankMoreFilterProps extends IBaseProps {
  isOperate: boolean;
  initialCheckAllCountries: boolean;
  regionOptions: IRegion[] | null;
  filter: IMobileStoreRankMoreFilter | IStoreRankMoreCoreFetchParams;
  onInit: () => void;
  onConfirm: (payload: IMobileStoreRankMoreFilter) => void;
  onReset: () => void;
  rangeTimeComponentDefault: any;
  buttonLogConfig?: Record<string, any>;
  entityType?: string;
}

/** MgrMoreFilter 组件实例接口 */
export interface IMgrMoreFilterInstance {
  setValueAndConfirm: (partialValue: Record<string, any>) => void;
}

const MobileStoreRankMoreFilter: React.ForwardRefRenderFunction<IMgrMoreFilterInstance, IMobileStoreRankMoreFilterProps> = (props, ref) => {
  const {
    style,
    className,
    isOperate,
    initialCheckAllCountries,
    regionOptions,
    filter,
    onInit,
    onConfirm,
    onReset,
    rangeTimeComponentDefault,
    entityType,
    filterLoading,
  } = props;

  const { filterExtend: filterExtendTmp, setFilterExtend, resetFilterExtend } = useFilterExtend({});
  const { tagConfig } = useSenTowerTag({});

  const initialFormValue = (() => {
    const singleSourceSelectDefault = FILTER_DEFAULT_VALUE[FILTER_KEYS.SINGLE_SOURCE_SELECT];
    const productModelDefault = FILTER_DEFAULT_VALUE[FILTER_KEYS.SENSOR_TOWER_TAG_PRODUCT_MODEL];
    if (!isOperate) return {
      ...FILTER_DEFAULT_VALUE,
      [FILTER_KEYS.SINGLE_SOURCE_SELECT]: {
        // value: singleSourceSelectFilterExtend?.value ?? singleSourceSelectDefault?.value,
        value: filter?.topchart_name ?? singleSourceSelectDefault?.value,
      },
    };
    const filterExtend = handleRangeDate2SingleDateRangeTimeFilterExtend(filterExtendTmp);
    const rangeTimeFilterExtend =
      filterExtend?.[FILTER_KEYS.RANGE_TIME];
    const composedCountrySelectFilterExtend = filterExtend?.[FILTER_KEYS.COMPOSED_COUNTRY_SELECT]?.value;
    const genreSelectFilterExtend = filterExtend?.[FILTER_KEYS.GENRETOR];
    const productModelFilterExtend = filterExtend?.[FILTER_KEYS.SENSOR_TOWER_TAG_PRODUCT_MODEL];

    const rangeTimeDefault = FILTER_DEFAULT_VALUE[FILTER_KEYS.RANGE_TIME];
    const releaseTimeDefault = FILTER_DEFAULT_VALUE[FILTER_KEYS.RELEASE_TIME];
    const composedCountrySelectDefault = FILTER_DEFAULT_VALUE[FILTER_KEYS.COMPOSED_COUNTRY_SELECT]?.value;
    const genreSelectDefault = FILTER_DEFAULT_VALUE[FILTER_KEYS.GENRETOR];

    return {
      [FILTER_KEYS.RANGE_TIME]: {
        type: rangeTimeFilterExtend?.filterType ?? rangeTimeDefault?.type,
        value: handleExtendTimeFilterValue({
          // 仅有天粒度，单选1天
          needSameDay: true,
          extendFilterValues: rangeTimeFilterExtend,
          defaultValue: rangeTimeDefault?.value,
        }),
      },
      [FILTER_KEYS.RELEASE_TIME]: {
        value: releaseTimeDefault?.value,
      },
      [FILTER_KEYS.SINGLE_SOURCE_SELECT]: {
        // value: singleSourceSelectFilterExtend?.value ?? singleSourceSelectDefault?.value,
        value: filter?.topchart_name ?? singleSourceSelectDefault?.value,
      },
      [FILTER_KEYS.COMPOSED_COUNTRY_SELECT]: {
        value: {
          type: composedCountrySelectFilterExtend?.type ?? composedCountrySelectDefault?.type,
          countries:
            composedCountrySelectFilterExtend?.countries?.length >= 1
              ? composedCountrySelectFilterExtend?.countries
              : composedCountrySelectDefault?.countries,
        },
      },
      [FILTER_KEYS.GENRETOR]: {
        source: genreSelectFilterExtend?.genretor?.source ?? genreSelectDefault?.source,
        mainGenres: genreSelectFilterExtend?.genretor?.mainGenres ?? genreSelectDefault?.mainGenres,
        subGenres: genreSelectFilterExtend?.genretor?.subGenres ?? genreSelectDefault?.subGenres,
        level3Genres: genreSelectFilterExtend?.genretor?.level3Genres ?? genreSelectDefault?.level3Genres,
        level4Genres: genreSelectFilterExtend?.genretor?.level4Genres ?? genreSelectDefault?.level4Genres,
      },
      [FILTER_KEYS.SENSOR_TOWER_TAG_PRODUCT_MODEL]: {
        value: productModelFilterExtend?.value ?? productModelDefault?.value ?? [],
      },
    };
  })();

  const {
    genreRef,
    genreSource,
    mainGenres,
    subGenres,
    level3Genres,
    level4Genres,

    handleGenresChange,
    handleReset: handleGenresReset,
    // getGenresReqParams,
    // updateGenres2Store,
  } = useGenreComponent(props, undefined, { defaultGenreSource: GENRE_SOURCE_MAP.IEGG });

  const [inputList, setInputList] = useState(FILTER_DEFAULT_INPUT_LIST);

  // const formValue = useRef<any>(initialFormValue);
  const [formValue, setFormValue] = useState(initialFormValue);

  const allCountries = useRef<string[]>([]);

  const formatFormValue = (values: any) => {
    const newFilterValues = formatFilterSaveValue({
      values,
      timeConfig: {
        // 天粒度单选1天
        needSameDay: true,
      },
    });
    return newFilterValues;
  };

  const {
    onSaveFilterMsg,
    onFilterChange,
  } = useFilterUseHook({
    defaultValue: FILTER_DEFAULT_VALUE,
    formValue,
    setFormValue,
    timeFilterValues: {
      type: 'daily',
      value: [filter?.start_date, filter?.end_date]
    },
    formatFormValue,
  });

  const handleInit = () => {
    onInit?.();
  };

  const handleConfirm = (value: any, apiValue: any) => {
    setFormValue(value);

    // 获取 Sensor Tower Tag 筛选器数据并转换为接口入参格式
    const sensorTowerTagParams = handleSensorTowerValueFilter(apiValue);

    const filterForm = {
      topchart_name: apiValue?.[FILTER_KEYS.SINGLE_SOURCE_SELECT]?.value,
      regions: apiValue?.[FILTER_KEYS.COMPOSED_COUNTRY_SELECT]?.value?.countries,

      genre: apiValue?.[FILTER_KEYS.GENRETOR]?.genre,
      sub_genre: apiValue?.[FILTER_KEYS.GENRETOR]?.sub_genre,
      genre_source: apiValue?.[FILTER_KEYS.GENRETOR]?.genre_source,
      iegg_genre_req: apiValue?.[FILTER_KEYS.GENRETOR]?.iegg_genre_req,

      start_date: apiValue?.[FILTER_KEYS.RANGE_TIME]?.start_date,
      end_date: apiValue?.[FILTER_KEYS.RANGE_TIME]?.end_date,
      start_release_time: apiValue?.[FILTER_KEYS.RELEASE_TIME]?.start_release_time ?? '',
      end_release_time: apiValue?.[FILTER_KEYS.RELEASE_TIME]?.end_release_time ?? '',

      sensortower_tag_filter: sensorTowerTagParams?.sensortower_tag_filter || [],
      // 传递原始品类数据，供 storeRank.ts 统一处理 filter 对象
      appannie_genre: apiValue[FILTER_KEYS.GENRETOR]?.appannie_genre || [],
      sensortower_genre: apiValue[FILTER_KEYS.GENRETOR]?.sensortower_genre || [],
      iegg_genre: apiValue[FILTER_KEYS.GENRETOR]?.iegg_genre || [],
      appmagic_genre: apiValue[FILTER_KEYS.GENRETOR]?.appmagic_genre || [],
      [FILTER_KEYS.SENSOR_TOWER_TAG_PRODUCT_MODEL]: apiValue[FILTER_KEYS.SENSOR_TOWER_TAG_PRODUCT_MODEL],
    };
    const { platform } = analysisPlatformTopInfo(filterForm?.topchart_name);
    const apiValueTmp = {
      ...apiValue,
      [FILTER_KEYS.SINGLE_PLATFORM_SELECT]: {
        value: platform,
      },
    };
    onConfirm?.(filterForm);

    // 点击确认后，将筛选器保存方案及保存数据存到缓存中
    onSaveFilterMsg(value, apiValue);
    // 榜单国家筛选器在confirm过滤继承值后需要更新继承值，
    // feat国家筛选闪动问题,setFilterExtend放在后面,
    setFilterExtend(apiValueTmp);

    if (!isListEqual(filter?.regions, filterForm.regions)) {
      updateURLSearchParams({}, ['isCheckAllCountries']);
    }
  };

  const handleReset = () => {
    const newFormValue = {
      ...FILTER_DEFAULT_VALUE,
      [FILTER_KEYS.RANGE_TIME]: {
        type: 'date',
        value: rangeTimeComponentDefault?.date
      },
      [FILTER_KEYS.COMPOSED_COUNTRY_SELECT]: {
        value: {
          type: 'multiple',
          countries: [...allCountries.current],
        },
      },
    };
    setFormValue(newFormValue);
    genreRef.current?.reset();
    resetFilterExtend();
    onReset?.();
  };

  useEffect(() => {
    if (filterLoading) return;
    const countries = getAllCountries(regionOptions ?? []);
    // 始终更新 allCountries.current，确保 defaultValue 中的 countries 不为空
    allCountries.current = [...countries];

    // 检查用户是否已经选择了单个国家
    // 如果 filter?.regions 是单个国家，说明用户已经选择了，不应该重置
    const userSelectedCountries = filter?.regions;
    const isUserSelectedSingleCountry =
      !initialCheckAllCountries &&
      userSelectedCountries &&
      Array.isArray(userSelectedCountries) &&
      userSelectedCountries.length === 1;

    // 如果用户已经选择了单个国家，直接返回，不执行重置逻辑
    // 避免覆盖用户的选择
    if (isUserSelectedSingleCountry) {
      return;
    }

    // 在初始化时或用户还没有选择时，设置 formValue 中的 countries
    // 确保 formValue 中的 countries 始终有值（用于 FilterBase 的 handleClickComfirm）
    const currentFormValueCountries = formValue?.[FILTER_KEYS.COMPOSED_COUNTRY_SELECT]?.value?.countries;

    // 如果 formValue 中已有 countries 且不是空数组，则保留；否则设置为所有国家
    let finalCountries = countries;
    if (
      !initialCheckAllCountries &&
      currentFormValueCountries &&
      Array.isArray(currentFormValueCountries) &&
      currentFormValueCountries.length > 0
    ) {
      finalCountries = currentFormValueCountries;
    }

    const newFormValue = {
      ...formValue,
      [FILTER_KEYS.COMPOSED_COUNTRY_SELECT]: {
        value: {
          ...formValue?.[FILTER_KEYS.COMPOSED_COUNTRY_SELECT]?.value,
          type: 'multiple',
          countries: finalCountries,
        },
      },
      [FILTER_KEYS.SINGLE_SOURCE_SELECT]: {
        ...formValue?.[FILTER_KEYS.SINGLE_SOURCE_SELECT],
        value: filter?.topchart_name ?? FILTER_DEFAULT_VALUE[FILTER_KEYS.SINGLE_SOURCE_SELECT],

      },
      [FILTER_KEYS.RANGE_TIME]: {
        ...formValue?.[FILTER_KEYS.RANGE_TIME],
        value: [moment(filter?.start_date), moment(filter?.end_date)],
      },
    };
    setFormValue(newFormValue);
  }, [
    filterLoading,
    isOperate,
    regionOptions,
    initialCheckAllCountries,
    filter?.regions,
    filter?.topchart_name,
    filter?.start_date,
    filter?.end_date,
  ]);

  const updateTimeComponentDefault = (inputListParam: any[]) => {
    const inputListTmp = inputListParam?.map((item) => {
      // 更新最早有数据时间
      if (rangeTimeComponentDefault) {
        if (item?.key === FILTER_KEYS.RANGE_TIME) {
          return {
            ...item,
            componentDefault: rangeTimeComponentDefault,
          };
        }
      }
      return item;
    });
    return inputListTmp;
  };

  useEffect(() => {
    let inputListTmp = [...inputList];
    const composedCountrySelectTmp = inputListTmp.find((item) => item?.key === FILTER_KEYS.COMPOSED_COUNTRY_SELECT);
    if (composedCountrySelectTmp) {
      // @ts-ignore
      composedCountrySelectTmp.options = regionOptions ?? [];
    }
    // 配置 Genre 筛选器 (四级品类)
    const genreSelectTmp = inputListTmp.find((item) => item?.key === FILTER_KEYS.GENRETOR);
    if (genreSelectTmp) {
      // @ts-ignore
      genreSelectTmp.genreData = {
        genreRef,
        genreSource,
        mainGenres,
        subGenres,
        level3Genres,
        level4Genres,
        handleGenresChange,
        handleGenresReset,
      };
      // @ts-ignore
      genreSelectTmp.entityType = entityType;
      // @ts-ignore
      genreSelectTmp.sources = GENRE_SOURCES_MULTI_DATASOURCE_TOPCHARTS_PAGE;
      // @ts-ignore 启用标准化品类参数处理，使 apiValue 包含 appmagic_genre 等字段
      genreSelectTmp.isStdGenreValuesHandler = true;
    }
    // 配置 Product Model 筛选器的 options
    const productModelTmp = inputListTmp.find((item) => item?.key === FILTER_KEYS.SENSOR_TOWER_TAG_PRODUCT_MODEL);
    if (productModelTmp) {
      // @ts-ignore
      productModelTmp.options = tagConfig?.[FILTER_KEYS.SENSOR_TOWER_TAG_PRODUCT_MODEL] || [];
    }
    inputListTmp = updateTimeComponentDefault(inputListTmp);

    setInputList(inputListTmp);
  }, [regionOptions, genreRef, genreSource, mainGenres, subGenres, level3Genres, level4Genres, handleGenresChange, handleGenresReset, rangeTimeComponentDefault, tagConfig]);

  const filterRef = useRef<IFilterInstance | null>(null);

  // 暴露 setValueAndConfirm 方法给父组件
  useImperativeHandle(ref, () => ({
    setValueAndConfirm: (partialValue: Record<string, any>) => {
      filterRef.current?.setValueAndConfirm(partialValue);
    },
  }));

  return (
    <Card style={style} className={classnames('dbi-mgrmorefilter', className)}>
      <Card.Body>
        <Filter
          ref={filterRef}
          showButton={true}
          inputList={inputList}
          defaultValue={{
            ...FILTER_DEFAULT_VALUE,
            [FILTER_KEYS.RANGE_TIME]: {
              type: 'date',
              value: rangeTimeComponentDefault?.date
            },
            [FILTER_KEYS.COMPOSED_COUNTRY_SELECT]: {
              value: {
                type: 'multiple',
                countries: [...(allCountries.current || [])],
              }
            }
          }}
          value={formValue}
          onInit={handleInit}
          onConfirm={handleConfirm}
          onReset={handleReset}
          onChange={onFilterChange}
          isHaveFilterSave={true}
        />
      </Card.Body>
    </Card>
  );
};

export default forwardRef(MobileStoreRankMoreFilter);
